import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { Plus, Trash2, Play, Pause } from "lucide-react";
import {
  fetchMarketingAutomations,
  createMarketingAutomation,
  deleteMarketingAutomation,
  activateMarketingAutomation,
  pauseMarketingAutomation,
} from "../../../../../../redux/actions/marketingAction";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const AutomationList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { automations, loading } = useSelector((state) => state.marketing);
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    trigger_event: "email_opened",
    condition: { type: "no_action_within", action: "signup", within_hours: 48 },
    action_type: "log_whatsapp",
    action_config: { message: "" },
  });

  useEffect(() => {
    dispatch(fetchMarketingAutomations({ page: page + 1 }));
  }, [dispatch, page]);

  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await dispatch(createMarketingAutomation(formData));
      setFormOpen(false);
      setFormData({
        name: "", trigger_event: "email_opened",
        condition: { type: "no_action_within", action: "signup", within_hours: 48 },
        action_type: "log_whatsapp", action_config: { message: "" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const items = automations?.items || [];
  const total = automations?.total || 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t('mkt_automations')} ({total})
        </Typography>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setFormOpen(true)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
          {t('mkt_new_automation')}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('common_name')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_trigger')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_condition')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('analytics_action')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_triggered')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5" }}>{t('mkt_actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>{t('mkt_no_automations')}</TableCell></TableRow>
              ) : items.map((auto) => (
                <TableRow key={auto.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{auto.name}</Typography></TableCell>
                  <TableCell><Chip label={auto.trigger_event.replace(/_/g, " ")} size="small" sx={{ borderRadius: "6px", fontSize: "0.7rem" }} /></TableCell>
                  <TableCell><Typography variant="caption">{auto.condition?.type}: {auto.condition?.action} within {auto.condition?.within_hours}h</Typography></TableCell>
                  <TableCell><Chip label={auto.action_type.replace(/_/g, " ")} size="small" variant="outlined" sx={{ borderRadius: "6px", fontSize: "0.7rem" }} /></TableCell>
                  <TableCell>
                    <Chip
                      label={auto.status}
                      size="small"
                      color={auto.status === "active" ? "success" : "default"}
                      sx={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>{auto.total_triggered}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {auto.status === "paused" ? (
                        <Tooltip title="Activate">
                          <IconButton size="small" onClick={() => dispatch(activateMarketingAutomation(auto.id))}>
                            <Play size={14} color="#10b981" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Pause">
                          <IconButton size="small" onClick={() => dispatch(pauseMarketingAutomation(auto.id))}>
                            <Pause size={14} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(auto)}>
                          <Trash2 size={14} color={theme.palette.error?.main} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        )}
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('mkt_new_automation')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t('common_name')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth size="small" required />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('mkt_trigger_event')}</InputLabel>
              <Select value={formData.trigger_event} onChange={(e) => setFormData({ ...formData, trigger_event: e.target.value })} label={t('mkt_trigger_event')}>
                {["email_opened", "email_clicked", "email_delivered", "signup", "trial_started", "page_visit"].map((evt) => (
                  <MenuItem key={evt} value={evt}>{evt.replace(/_/g, " ")}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('mkt_condition_type')}</InputLabel>
              <Select value={formData.condition.type} onChange={(e) => setFormData({ ...formData, condition: { ...formData.condition, type: e.target.value } })} label={t('mkt_condition_type')}>
                <MenuItem value="always">{t('mkt_always')}</MenuItem>
                <MenuItem value="no_action_within">{t('mkt_no_action_within')}</MenuItem>
                <MenuItem value="has_action_within">{t('mkt_has_action_within')}</MenuItem>
                <MenuItem value="score_above">{t('mkt_score_above')}</MenuItem>
              </Select>
            </FormControl>
            {["no_action_within", "has_action_within"].includes(formData.condition.type) && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField label={t('mkt_action_to_check')} value={formData.condition.action} onChange={(e) => setFormData({ ...formData, condition: { ...formData.condition, action: e.target.value } })} size="small" sx={{ flex: 1 }} />
                <TextField label={t('mkt_within_hours')} type="number" value={formData.condition.within_hours} onChange={(e) => setFormData({ ...formData, condition: { ...formData.condition, within_hours: parseInt(e.target.value) } })} size="small" sx={{ width: 140 }} />
              </Box>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>{t('mkt_action_type')}</InputLabel>
              <Select value={formData.action_type} onChange={(e) => setFormData({ ...formData, action_type: e.target.value })} label={t('mkt_action_type')}>
                <MenuItem value="send_email">{t('mkt_send_email')}</MenuItem>
                <MenuItem value="log_whatsapp">{t('mkt_log_whatsapp')}</MenuItem>
                <MenuItem value="add_tag">{t('mkt_add_tag')}</MenuItem>
                <MenuItem value="update_score">{t('mkt_update_score')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)}>{t('common_cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formData.name || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t('mkt_creating') : t('mkt_create')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await dispatch(deleteMarketingAutomation(deleteTarget.id));
          setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name}
        itemType={t("mkt_automation")}
      />
    </Box>
  );
};

export default AutomationList;
